import { useState } from "react"
import useTradingDataQuery from "../Portfolio/hooks/useTradingDataQuery"
import { IAccountBookPairs } from "../types"
import AllocationCard from "./components/AllocationCard"
import DistributionPie from "./components/DistributionPie"
import GroupCard from "./components/GroupCard"
import GroupSelector from "./components/GroupSelector"
import Top10Card from "./components/Top10Card"
import {
  FactsheetContainer,
  GroupCardsGrid,
  MasonryColumn,
  PieChartsRow,
  SkeletonContainer,
  SummaryRow,
} from "./Factsheet.styled"
import { useFactsheetData } from "./hooks/useFactsheetData"
import { distributeIntoColumns, useMasonryColumns } from "./hooks/useMasonryColumns"
import { GroupingDimension } from "./types"

interface FactsheetProps {
  selectedFilters: IAccountBookPairs
  enableTradingQuery: boolean
}

const Factsheet = ({ selectedFilters, enableTradingQuery }: FactsheetProps) => {
  const [grouping, setGrouping] = useState<GroupingDimension>("sector_l1")
  const { containerRef, numColumns } = useMasonryColumns(280, 12)

  const { tradingData, isLoading } = useTradingDataQuery({
    accountBookPairs: selectedFilters,
    liveOrders: false,
    enabled: enableTradingQuery,
    withPnl: false,
    useAccountCurrency: true,
  })

  const { summary, groupedData } = useFactsheetData(tradingData?.positions?.rows, grouping)
  const columns = distributeIntoColumns(groupedData, numColumns)

  if (isLoading) {
    return (
      <FactsheetContainer flexDirection="column">
        <SummaryRow>
          <SkeletonContainer height={180} />
          <SkeletonContainer height={180} />
        </SummaryRow>
        <PieChartsRow>
          <SkeletonContainer height={200} width={300} />
          <SkeletonContainer height={200} width={300} />
          <SkeletonContainer height={200} width={300} />
        </PieChartsRow>
        <GroupSelector selected={grouping} onChange={setGrouping} />
        <GroupCardsGrid ref={containerRef}>
          {Array.from({ length: numColumns || 4 }).map((_, colIndex) => (
            <MasonryColumn key={colIndex}>
              <SkeletonContainer height={200} />
              <SkeletonContainer height={150} />
            </MasonryColumn>
          ))}
        </GroupCardsGrid>
      </FactsheetContainer>
    )
  }

  if (!tradingData?.positions?.rows?.length) {
    return (
      <FactsheetContainer flexDirection="column" justifyContent="center" alignItems="center">
        No positions data available
      </FactsheetContainer>
    )
  }

  return (
    <FactsheetContainer flexDirection="column">
      {/* Row 1: Summary Cards */}
      <SummaryRow>
        <AllocationCard allocation={summary.allocation} currency={tradingData?.display_currency} />
        <Top10Card positions={summary.top10} currency={tradingData?.display_currency} />
      </SummaryRow>

      {/* Row 2: Pie Charts */}
      <PieChartsRow>
        <DistributionPie title="Geography" data={summary.geoBreakdown} />
        <DistributionPie title="Sector L1" data={summary.sectorBreakdown} />
        <DistributionPie title="Currency" data={summary.currencyBreakdown} />
      </PieChartsRow>

      {/* Row 3: Group Selector */}
      <GroupSelector selected={grouping} onChange={setGrouping} />

      {/* Row 4: Grouped Cards in Masonry Grid */}
      <GroupCardsGrid ref={containerRef}>
        {columns.map((columnItems, colIndex) => (
          <MasonryColumn key={colIndex}>
            {columnItems.map(group => (
              <GroupCard key={group.name} group={group} currency={tradingData?.display_currency} />
            ))}
          </MasonryColumn>
        ))}
      </GroupCardsGrid>
    </FactsheetContainer>
  )
}

export default Factsheet
